/// <amd-module name="Repos/Views/CreatePullRequest/RestClient/Git" />
/**
 * ---------------------------------------------------------
 * Generated file, DO NOT EDIT
 * ---------------------------------------------------------
 *
 * See following wiki page for instructions on how to regenerate:
 *   https://aka.ms/azure-devops-client-generation
 *
 * Configuration file:
 *   tfs\web\extensions\code\vss-code-web\repos\views\pr-create\genclient.json
 */

import { IVssPageContext, IVssRestClient, IVssRestClientOptions, RestClients } from "VSS/Platform/Context";
import { RestClientBase } from "VSS/Platform/RestClientBase";
import { deserializeVssJsonObject } from "VSS/Platform/Util/Serialization";

import * as Git from "Repos/Common/Contracts/Git";
import * as VS_Services from "VSS/Platform/Contracts/WebApi";

export interface IGitRestClient extends IVssRestClient {
    /**
     * Retrieve a particular commit.
     * 
     * @param commitId - The id of the commit.
     * @param repositoryId - The id or friendly name of the repository. To use the friendly name, projectId must also be specified.
     * @param project - Project ID or project name
     * @param changeCount - The number of changes to include in the result.
     */
    getCommit(commitId: string, repositoryId: string, project?: string, changeCount?: number): Promise<Git.GitCommit>;
    /**
     * Retrieve git commits for a project matching the search criteria
     * 
     * @param searchCriteria - Search options
     * @param repositoryId - The name or ID of the repository.
     * @param project - Project ID or project name
     * @param skip - Number of commits to skip. The value cannot exceed 3,000,000.
     * @param top - Maximum number of commits to return. The value cannot exceed 50,000.
     * @param includeStatuses - True to include additional commit status information.
     */
    getCommitsBatch(searchCriteria: Git.GitQueryCommitsCriteria, repositoryId: string, project?: string, skip?: number, top?: number, includeStatuses?: boolean): Promise<Git.GitCommitRef[]>;
    /**
     * Get Item Metadata and/or Content for a single item. The download parameter is to indicate whether the content should be available as a download or just sent as a stream in the response. Doesn't apply to zipped content, which is always returned as a download.
     * 
     * @param repositoryId - The name or ID of the repository.
     * @param path - The item path.
     * @param project - Project ID or project name
     * @param scopePath - The path scope.  The default is null.
     * @param recursionLevel - The recursion level of this request. The default is 'none', no recursion.
     * @param includeContentMetadata - Set to true to include content metadata.  Default is false.
     * @param latestProcessedChange - Set to true to include the latest changes.  Default is false.
     * @param download - Set to true to download the response as a file.  Default is false.
     * @param versionDescriptor - Version descriptor.  Default is the default branch for the repository.
     * @param includeContent - Set to true to include item content when requesting json.  Default is false.
     * @param resolveLfs - Set to true to resolve Git LFS pointer files to return actual content from Git LFS.  Default is false.
     * @param sanitize - Set to true to sanitize an svg file and return it as image. Useful only if requested for svg file. Default is false.
     */
    getItemText(repositoryId: string, path: string, project?: string, scopePath?: string, recursionLevel?: Git.VersionControlRecursionType, includeContentMetadata?: boolean, latestProcessedChange?: boolean, download?: boolean, versionDescriptor?: Git.GitVersionDescriptor, includeContent?: boolean, resolveLfs?: boolean, sanitize?: boolean): Promise<string>;
    /**
     * Find the merge bases of two commits, optionally across forks. If otherRepositoryId is not specified, the merge bases will only be calculated within the context of the local repositoryNameOrId.
     * 
     * @param repositoryNameOrId - ID or name of the local repository.
     * @param commitId - First commit, usually the tip of the target branch of the potential merge.
     * @param otherCommitId - Other commit, usually the tip of the source branch of the potential merge.
     * @param project - Project ID or project name
     * @param otherCollectionId - The collection ID where otherCommitId lives.
     * @param otherRepositoryId - The repository ID where otherCommitId lives.
     */
    getMergeBases(repositoryNameOrId: string, commitId: string, otherCommitId: string, project?: string, otherCollectionId?: string, otherRepositoryId?: string): Promise<Git.GitCommitRef[]>;
    /**
     * Create a pull request.
     * 
     * @param gitPullRequestToCreate - The pull request to create.
     * @param repositoryId - The repository ID of the pull request's target branch.
     * @param project - Project ID or project name
     * @param supportsIterations - If true, subsequent pushes to the pull request will be individually reviewable. Set this to false for large pull requests for performance reasons if this functionality is not needed.
     */
    createPullRequest(gitPullRequestToCreate: Git.GitPullRequest, repositoryId: string, project?: string, supportsIterations?: boolean): Promise<Git.GitPullRequest>;
    /**
     * Update a pull request
     * 
     * @param gitPullRequestToUpdate - The pull request content that should be updated.
     * @param repositoryId - The repository ID of the pull request's target branch.
     * @param pullRequestId - ID of the pull request to update.
     * @param project - Project ID or project name
     */
    updatePullRequest(gitPullRequestToUpdate: Git.GitPullRequest, repositoryId: string, pullRequestId: number, project?: string): Promise<Git.GitPullRequest>;
    /**
     * Queries the provided repository for its refs and returns them.
     * 
     * @param repositoryId - The name or ID of the repository.
     * @param project - Project ID or project name
     * @param filter - [optional] A filter to apply to the refs (starts with).
     * @param includeLinks - [optional] Specifies if referenceLinks should be included in the result. default is false.
     * @param includeStatuses - [optional] Includes up to the first 1000 commit statuses for each ref. The default value is false.
     * @param includeMyBranches - [optional] Includes only branches that the user owns, the branches the user favorites, and the default branch. The default value is false. Cannot be combined with the filter parameter.
     * @param latestStatusesOnly - [optional] True to include only the tip commit status for each ref. This option requires \`includeStatuses\` to be true. The default value is false.
     * @param peelTags - [optional] Annotated tags will populate the PeeledObjectId property. default is false.
     * @param filterContains - [optional] A filter to apply to the refs (contains).
     * @param top - [optional] Maximum number of refs to return. It cannot be bigger than 1000. If it is not provided but continuationToken is, top will default to 100.
     * @param continuationToken - The continuation token used for pagination.
     */
    getRefs(repositoryId: string, project?: string, filter?: string, includeLinks?: boolean, includeStatuses?: boolean, includeMyBranches?: boolean, latestStatusesOnly?: boolean, peelTags?: boolean, filterContains?: string, top?: number, continuationToken?: string): Promise<VS_Services.PagedList<Git.GitRef>>;
}

class GitRestClient extends RestClientBase implements IGitRestClient {
    constructor(options: IVssRestClientOptions) {
        super(options);
    }

    /**
     * Retrieve a particular commit.
     * 
     * @param commitId - The id of the commit.
     * @param repositoryId - The id or friendly name of the repository. To use the friendly name, projectId must also be specified.
     * @param project - Project ID or project name
     * @param changeCount - The number of changes to include in the result.
     */
    public async getCommit(
        commitId: string,
        repositoryId: string,
        project?: string,
        changeCount?: number
        ): Promise<Git.GitCommit> {

        const queryValues: any = {
            changeCount: changeCount
        };

        return this.beginRequest<Git.GitCommit>({
            apiVersion: "5.0-preview.1",
            routeTemplate: "{project}/_apis/git/repositories/{repositoryId}/Commits/{commitId}",
            routeValues: {
                project: project,
                commitId: commitId,
                repositoryId: repositoryId
            },
            queryParams: queryValues
        });
    }

    /**
     * Retrieve git commits for a project matching the search criteria
     * 
     * @param searchCriteria - Search options
     * @param repositoryId - The name or ID of the repository.
     * @param project - Project ID or project name
     * @param skip - Number of commits to skip. The value cannot exceed 3,000,000.
     * @param top - Maximum number of commits to return. The value cannot exceed 50,000.
     * @param includeStatuses - True to include additional commit status information.
     */
    public async getCommitsBatch(
        searchCriteria: Git.GitQueryCommitsCriteria,
        repositoryId: string,
        project?: string,
        skip?: number,
        top?: number,
        includeStatuses?: boolean
        ): Promise<Git.GitCommitRef[]> {

        const queryValues: any = {
            '$skip': skip,
            '$top': top,
            includeStatuses: includeStatuses
        };

        return this.beginRequest<Git.GitCommitRef[]>({
            apiVersion: "5.0-preview.1",
            method: "POST",
            routeTemplate: "{project}/_apis/git/repositories/{repositoryId}/CommitsBatch",
            routeValues: {
                project: project,
                repositoryId: repositoryId
            },
            queryParams: queryValues,
            body: searchCriteria
        });
    }

    /**
     * Get Item Metadata and/or Content for a single item. The download parameter is to indicate whether the content should be available as a download or just sent as a stream in the response. Doesn't apply to zipped content, which is always returned as a download.
     * 
     * @param repositoryId - The name or ID of the repository.
     * @param path - The item path.
     * @param project - Project ID or project name
     * @param scopePath - The path scope.  The default is null.
     * @param recursionLevel - The recursion level of this request. The default is 'none', no recursion.
     * @param includeContentMetadata - Set to true to include content metadata.  Default is false.
     * @param latestProcessedChange - Set to true to include the latest changes.  Default is false.
     * @param download - Set to true to download the response as a file.  Default is false.
     * @param versionDescriptor - Version descriptor.  Default is the default branch for the repository.
     * @param includeContent - Set to true to include item content when requesting json.  Default is false.
     * @param resolveLfs - Set to true to resolve Git LFS pointer files to return actual content from Git LFS.  Default is false.
     * @param sanitize - Set to true to sanitize an svg file and return it as image. Useful only if requested for svg file. Default is false.
     */
    public async getItemText(
        repositoryId: string,
        path: string,
        project?: string,
        scopePath?: string,
        recursionLevel?: Git.VersionControlRecursionType,
        includeContentMetadata?: boolean,
        latestProcessedChange?: boolean,
        download?: boolean,
        versionDescriptor?: Git.GitVersionDescriptor,
        includeContent?: boolean,
        resolveLfs?: boolean,
        sanitize?: boolean
        ): Promise<string> {

        const queryValues: any = {
            path: path,
            scopePath: scopePath,
            recursionLevel: recursionLevel,
            includeContentMetadata: includeContentMetadata,
            latestProcessedChange: latestProcessedChange,
            download: download,
            versionDescriptor: versionDescriptor,
            includeContent: includeContent,
            resolveLfs: resolveLfs,
            sanitize: sanitize
        };

        return this.beginRequest<string>({
            apiVersion: "5.0-preview.1",
            httpResponseType: "text/plain",
            routeTemplate: "{project}/_apis/git/repositories/{repositoryId}/Items/{*path}",
            routeValues: {
                project: project,
                repositoryId: repositoryId
            },
            queryParams: queryValues
        });
    }

    /**
     * Find the merge bases of two commits, optionally across forks. If otherRepositoryId is not specified, the merge bases will only be calculated within the context of the local repositoryNameOrId.
     * 
     * @param repositoryNameOrId - ID or name of the local repository.
     * @param commitId - First commit, usually the tip of the target branch of the potential merge.
     * @param otherCommitId - Other commit, usually the tip of the source branch of the potential merge.
     * @param project - Project ID or project name
     * @param otherCollectionId - The collection ID where otherCommitId lives.
     * @param otherRepositoryId - The repository ID where otherCommitId lives.
     */
    public async getMergeBases(
        repositoryNameOrId: string,
        commitId: string,
        otherCommitId: string,
        project?: string,
        otherCollectionId?: string,
        otherRepositoryId?: string
        ): Promise<Git.GitCommitRef[]> {

        const queryValues: any = {
            otherCommitId: otherCommitId,
            otherCollectionId: otherCollectionId,
            otherRepositoryId: otherRepositoryId
        };

        return this.beginRequest<Git.GitCommitRef[]>({
            apiVersion: "5.0-preview.1",
            routeTemplate: "{project}/_apis/git/repositories/{repositoryNameOrId}/commits/{commitId}/mergeBases",
            routeValues: {
                project: project,
                repositoryNameOrId: repositoryNameOrId,
                commitId: commitId
            },
            queryParams: queryValues
        });
    }

    /**
     * Create a pull request.
     * 
     * @param gitPullRequestToCreate - The pull request to create.
     * @param repositoryId - The repository ID of the pull request's target branch.
     * @param project - Project ID or project name
     * @param supportsIterations - If true, subsequent pushes to the pull request will be individually reviewable. Set this to false for large pull requests for performance reasons if this functionality is not needed.
     */
    public async createPullRequest(
        gitPullRequestToCreate: Git.GitPullRequest,
        repositoryId: string,
        project?: string,
        supportsIterations?: boolean
        ): Promise<Git.GitPullRequest> {

        const queryValues: any = {
            supportsIterations: supportsIterations
        };

        return this.beginRequest<Git.GitPullRequest>({
            apiVersion: "5.0-preview.1",
            method: "POST",
            routeTemplate: "{project}/_apis/git/repositories/{repositoryId}/pullRequests/{pullRequestId}",
            routeValues: {
                project: project,
                repositoryId: repositoryId
            },
            queryParams: queryValues,
            body: gitPullRequestToCreate
        });
    }

    /**
     * Update a pull request
     * 
     * @param gitPullRequestToUpdate - The pull request content that should be updated.
     * @param repositoryId - The repository ID of the pull request's target branch.
     * @param pullRequestId - ID of the pull request to update.
     * @param project - Project ID or project name
     */
    public async updatePullRequest(
        gitPullRequestToUpdate: Git.GitPullRequest,
        repositoryId: string,
        pullRequestId: number,
        project?: string
        ): Promise<Git.GitPullRequest> {

        return this.beginRequest<Git.GitPullRequest>({
            apiVersion: "5.0-preview.1",
            method: "PATCH",
            routeTemplate: "{project}/_apis/git/repositories/{repositoryId}/pullRequests/{pullRequestId}",
            routeValues: {
                project: project,
                repositoryId: repositoryId,
                pullRequestId: pullRequestId
            },
            body: gitPullRequestToUpdate
        });
    }

    /**
     * Queries the provided repository for its refs and returns them.
     * 
     * @param repositoryId - The name or ID of the repository.
     * @param project - Project ID or project name
     * @param filter - [optional] A filter to apply to the refs (starts with).
     * @param includeLinks - [optional] Specifies if referenceLinks should be included in the result. default is false.
     * @param includeStatuses - [optional] Includes up to the first 1000 commit statuses for each ref. The default value is false.
     * @param includeMyBranches - [optional] Includes only branches that the user owns, the branches the user favorites, and the default branch. The default value is false. Cannot be combined with the filter parameter.
     * @param latestStatusesOnly - [optional] True to include only the tip commit status for each ref. This option requires \`includeStatuses\` to be true. The default value is false.
     * @param peelTags - [optional] Annotated tags will populate the PeeledObjectId property. default is false.
     * @param filterContains - [optional] A filter to apply to the refs (contains).
     * @param top - [optional] Maximum number of refs to return. It cannot be bigger than 1000. If it is not provided but continuationToken is, top will default to 100.
     * @param continuationToken - The continuation token used for pagination.
     */
    public async getRefs(
        repositoryId: string,
        project?: string,
        filter?: string,
        includeLinks?: boolean,
        includeStatuses?: boolean,
        includeMyBranches?: boolean,
        latestStatusesOnly?: boolean,
        peelTags?: boolean,
        filterContains?: string,
        top?: number,
        continuationToken?: string
        ): Promise<VS_Services.PagedList<Git.GitRef>> {

        const queryValues: any = {
            filter: filter,
            includeLinks: includeLinks,
            includeStatuses: includeStatuses,
            includeMyBranches: includeMyBranches,
            latestStatusesOnly: latestStatusesOnly,
            peelTags: peelTags,
            filterContains: filterContains,
            '$top': top,
            continuationToken: continuationToken
        };

        return this.beginRequest<Response>({
            apiVersion: "5.0-preview.1",
            routeTemplate: "{project}/_apis/git/repositories/{repositoryId}/refs/{*filter}",
            routeValues: {
                project: project,
                repositoryId: repositoryId
            },
            queryParams: queryValues,
            returnRawResponse: true
        }).then(async response => {
            const body = <VS_Services.PagedList<Git.GitRef>>await response.text().then(deserializeVssJsonObject);
            body.continuationToken = response.headers.get("x-ms-continuationtoken");
            return body;
        });
    }

}

export const GitClientName = "Repos.PRCreate.Git.IGitRestClient";

export function getGitClient(pageContext: IVssPageContext, options?: IVssRestClientOptions): IGitRestClient {
    return pageContext.getRestClient<IGitRestClient>(GitClientName, options);
}

RestClients.add(GitClientName, { 
    factory: GitRestClient,
    options: {
        resourceAreaId: "4e080c62-fa21-4fbc-8fef-2a10a2b38049",
        serviceInstanceType: "00025394-6065-48ca-87d9-7f5672854ef7"
    }
});
